import React, { useCallback, useEffect, useState } from 'react';

import { DeleteOutlined } from '@ant-design/icons';
import { Button as AntBtn, Card, Form, Select } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getBankDetailEditRoute,
  getPromoterEditRoute,
  getPromoterSettingRoute,
} from 'routes/user-management/list';
import { Button, Input, Label, Table, Tabs, Typography, message } from 'ui';

import {
  useCreatePromoterSettingsMutation,
  useDeletePromoterSettingsMutation,
  useLazyGetPromoterInfoQuery,
} from 'services/promoter/promoterApiService';

import { PROMOTER_DEFAULT_SETTING_TYPE_ENUM } from 'types/core/systemTypes';

import s from './PromoterSetting.module.scss';

const { Option } = Select;

interface Setting {
  type: PROMOTER_DEFAULT_SETTING_TYPE_ENUM;
  value: string | number;
}

const allTypes: PROMOTER_DEFAULT_SETTING_TYPE_ENUM[] = [
  PROMOTER_DEFAULT_SETTING_TYPE_ENUM.EventVATRate,
  PROMOTER_DEFAULT_SETTING_TYPE_ENUM.PaymentGatewayType,
  PROMOTER_DEFAULT_SETTING_TYPE_ENUM.BrevoAPIKey,
];

const gatewayOptions: Record<number, string> = {
  1: 'Apco',
  2: 'Trust',
  3: 'GPG',
  4: 'Stripe',
  5: 'VIVA',
};

const typeLabels: Record<PROMOTER_DEFAULT_SETTING_TYPE_ENUM, string> = {
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.EventVATRate]: 'Event VAT Rate',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.PaymentGatewayType]: 'Payment Gateway Type',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.BrevoAPIKey]: 'Brevo API Key',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.CreditCardBookingFeeRate]: 'Credit Card Booking Fee Rate',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.PayPalBookingFeeRate]: 'PayPal Booking Fee Rate',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.CashBookingFeeRate]: 'Cash Booking Fee Rate',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.EventRate]: 'Event Rate',
  [PROMOTER_DEFAULT_SETTING_TYPE_ENUM.ShowsHappeningPercentBookingFeeShare]:
    'Shows Happening % Booking Fee Share',
};

const getDisplayValue = (setting: Setting) => {
  if (setting.type === PROMOTER_DEFAULT_SETTING_TYPE_ENUM.PaymentGatewayType) {
    return gatewayOptions[Number(setting.value)] ?? `Unknown (${setting.value})`;
  }
  return setting.value;
};

const PromoterSetting: React.FC = () => {
  const { promoterId } = useParams();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [usedTypes, setUsedTypes] = useState<PROMOTER_DEFAULT_SETTING_TYPE_ENUM[]>([]);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [getPromoterFullInfo, { data: promoterData, isFetching }] = useLazyGetPromoterInfoQuery();

  const [createPromoterSettings, { isLoading }] = useCreatePromoterSettingsMutation();
  const [deletePromoterSettings, { isLoading: deleting }] = useDeletePromoterSettingsMutation();

  const selectedType: PROMOTER_DEFAULT_SETTING_TYPE_ENUM | undefined = Form.useWatch('type', form);

  //   const usedTypes = settings.map((s) => s.type);
  const availableTypes = allTypes.filter((t) => !usedTypes.includes(t));

  const handleAdd = (values: Setting) => {
    const value =
      selectedType === PROMOTER_DEFAULT_SETTING_TYPE_ENUM.EventVATRate
        ? Number(values.value)
        : values.value;

    setSettings((prev) => {
      const updated = [...prev, { type: values.type, value }];
      setUsedTypes(updated.map((s) => s.type)); // use updated list
      return updated;
    });
    form.resetFields();
    message.success('Setting added');
  };

  const handleSaveAll = async () => {
    if (!promoterId || promoterId === 'create') {
      message.warning('Cannot save without a valid promoter ID.');
      return;
    }

    try {
      // replace with your actual API call
      const res = await createPromoterSettings({ id: promoterId, settings: settings });

      if ('data' in res) {
        message.success('Settings saved successfully');
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to save settings');
    }
  };

  const handleDelete = async (typeToDelete: PROMOTER_DEFAULT_SETTING_TYPE_ENUM) => {
    if (!promoterId || promoterId === 'create') {
      message.warning('Cannot delete without a valid promoter ID.');
      return;
    }

    try {
      // replace with your actual API call
      const res = await deletePromoterSettings({ id: promoterId, type: typeToDelete });

      if ('data' in res) {
        message.success('Settings deleted successfully');
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to save settings');
    }

    setSettings((prev) => {
      const updated = prev.filter((s) => s.type !== typeToDelete);
      setUsedTypes(updated.map((s) => s.type)); // use updated list
      return updated;
    });
  };

  const renderValueField = () => {
    switch (selectedType) {
      case PROMOTER_DEFAULT_SETTING_TYPE_ENUM.EventVATRate:
        return (
          <Form.Item name="value" rules={[{ required: true, message: 'Please enter VAT rate' }]}>
            <Input.Number
              min={1}
              max={100}
              formatter={(val) => `${val}%`}
              parser={(val) => val?.replace('%', '') || ''}
              placeholder="e.g. 20"
              style={{ width: 150 }}
            />
          </Form.Item>
        );

      case PROMOTER_DEFAULT_SETTING_TYPE_ENUM.PaymentGatewayType:
        return (
          <Form.Item name="value" rules={[{ required: true, message: 'Please select a gateway' }]}>
            <Select placeholder="Select gateway" style={{ width: 150 }}>
              <Option value={1}>Apco</Option>
              <Option value={2}>Trust</Option>
              <Option value={3}>GPG</Option>
              <Option value={4}>Stripe</Option>
              <Option value={5}>VIVA</Option>
            </Select>
          </Form.Item>
        );

      case PROMOTER_DEFAULT_SETTING_TYPE_ENUM.BrevoAPIKey:
        return (
          <Form.Item
            className={s.brevoInput}
            name="value"
            rules={[
              { required: true, message: 'Please enter API key' },
              {
                max: 100,
                message: 'Maximum length is 100 characters',
              },
            ]}>
            <Input name="value" placeholder="Paste API key here" />
          </Form.Item>
        );

      default:
        return null;
    }
  };

  const goToOrganiserPage = useCallback(() => {
    navigate(getPromoterEditRoute(promoterId && promoterId !== 'create' ? promoterId : 'create'));
  }, [promoterId, navigate]);

  const goToBillingPage = useCallback(() => {
    navigate(getBankDetailEditRoute(promoterId && promoterId !== 'create' ? promoterId : 'create'));
  }, [promoterId, navigate]);

  const goToPromoterPage = useCallback(() => {
    navigate(
      getPromoterSettingRoute(promoterId && promoterId !== 'create' ? promoterId : 'create'),
    );
  }, [promoterId, navigate]);

  useEffect(() => {
    if (promoterId) {
      getPromoterFullInfo(promoterId);
    }
  }, [promoterId, getPromoterFullInfo]);

  useEffect(() => {
    if (!promoterData?.defaultSettings) return;

    const transformed: Setting[] = promoterData?.defaultSettings.map((item) => {
      const type = item.settingType as PROMOTER_DEFAULT_SETTING_TYPE_ENUM;

      let value: string | number = item.value;

      if (type === PROMOTER_DEFAULT_SETTING_TYPE_ENUM.PaymentGatewayType) {
        value = Number(item.value); // ✅ fix here
      }

      if (type === PROMOTER_DEFAULT_SETTING_TYPE_ENUM.EventVATRate) {
        value = Number(item.value); // ✅ also here
      }

      return { type, value };
    });

    setSettings(transformed);
    setUsedTypes(transformed.map((s) => s.type));
  }, [promoterData?.defaultSettings]);

  const tabItems = [
    {
      key: 'organiser',
      label: 'Organiser details',
      onClick: goToOrganiserPage,
      isActive: location.pathname.includes('/organiser-details'), // Adjust match logic if needed
    },
    {
      key: 'billing',
      label: 'Billing details',
      onClick: goToBillingPage,
      isActive: location.pathname.includes('/bank-details'),
    },
    {
      key: 'promoter',
      label: 'Promoter settings',
      onClick: goToPromoterPage,
      isActive: location.pathname.includes('/promoter-setting'),
    },
  ];

  // Determine active key
  const activeKey = tabItems.find((item) => item.isActive)?.key ?? 'promoter';

  const handleTabChange = (key: string) => {
    const tab = tabItems.find((t) => t.key === key);
    tab?.onClick();
  };

  return (
    <div className={s.wrapper}>
      <Typography type="h2" className={s.title}>
        Settings
      </Typography>
      <Tabs
        activeKey={activeKey}
        defaultActiveKey="promoter"
        onChange={handleTabChange}
        items={tabItems.map(({ key, label }) => ({
          key,
          label,
        }))}
        tabBarStyle={{ marginBottom: 20 }}
      />

      <Label>
        Add new settings here. Don&apos;t forget to click &apos;Save All Settings&apos; when
        you&apos;re ready.
      </Label>

      {availableTypes.length > 0 && (
        <Card title="Add Setting" size="small">
          <Form layout="inline" className={s.form} form={form} onFinish={handleAdd}>
            <Form.Item
              name="type"
              className={s.typeSelect}
              rules={[{ required: true, message: 'Please select a type' }]}>
              <Select placeholder="Select type">
                {availableTypes.map((type) => (
                  <Option key={type} value={type}>
                    {typeLabels[type] || type}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {renderValueField()}

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Add
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      <Table
        rowKey="type"
        style={{ marginTop: 24 }}
        dataSource={settings}
        loading={isFetching || deleting}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type: PROMOTER_DEFAULT_SETTING_TYPE_ENUM) => typeLabels[type] || type,
          },
          {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            render: (_: any, record: Setting) => getDisplayValue(record),
          },
          {
            title: 'Action',
            key: 'action',
            render: (_: any, record: Setting) => (
              <AntBtn
                style={{ paddingLeft: 0 }}
                danger
                type="link"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.type)}>
                Delete
              </AntBtn>
            ),
          },
        ]}
      />

      {settings.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Button
            type="primary"
            loading={isLoading}
            onClick={handleSaveAll}
            disabled={settings.length === 0}>
            Save All Settings
          </Button>
        </div>
      )}
    </div>
  );
};

export default PromoterSetting;
